import { isNumber, parseNumber } from 'intl-utils';
import { onModuleLoad } from 'runtime';
import { fetch } from 'utils';

const navigationType = performance.getEntriesByType('navigation')[0]?.type;
const shouldRestoreScroll = navigationType === 'back_forward';

let isRestoringScroll = shouldRestoreScroll;

// Disable browser auto scroll restoration (if applicable).
if (shouldRestoreScroll && 'scrollRestoration' in history)
    history.scrollRestoration = 'manual';

// Save scroll position continuously.
window.addEventListener('scroll', () => {
    // Make sure we don't update the session storage before the scroll restoration is applied (if applicable).
    if (isRestoringScroll)
        return;

    sessionStorage.setItem(`scrollY:${getScrollKey()}`, window.scrollY);
});

onModuleLoad(import.meta, async () => {
    const productListings = document.querySelector('#product-listings');
    
    // Fixes the inifinite scroll when used on an ajax-list after the filter is updated.
    productListings.addEventListener('list:loaded:viewUpdated', e => {
        const results = productListings.querySelector('.results');

        if (results) {
            window.scrollTo({
                top: Math.max(
                    0,
                    results.getBoundingClientRect().top + window.scrollY - 150
                ),
                behavior: 'instant'
            });
        }

        const pager = productListings.querySelector('.infinite-scroll');

        if (pager) {
            initInfiniteScroll(pager);
        }
    });

    const savedPage = shouldRestoreScroll ? sessionStorage.getItem(`currentPage:${getScrollKey()}`) : null;
    const savedScroll = shouldRestoreScroll ? sessionStorage.getItem(`scrollY:${getScrollKey()}`) : null;

    await initInfiniteScroll(productListings.querySelector('.infinite-scroll'), savedPage != null ? parseNumber(savedPage) : null);

    if (!savedScroll) {
        isRestoringScroll = false;
        return;
    }

    requestAnimationFrame(() => {
        window.scrollTo(0, parseNumber(savedScroll));

        // Allow saving *after* restoration is applied
        isRestoringScroll = false;
    });
});

// Make sure it gets the latest value when the filter changes.
function getScrollKey() {
    return `${location.pathname}${location.search}`;
}

async function initInfiniteScroll(element, savedPage = null) {
    // Hide the pager. Do not use display none otherwise getting the rect below won't work.
    element.style.visibility = 'hidden';

    const target = element.dataset.infiniteScrollTarget;
    const targetElement = document.querySelector(target);

    const container = targetElement?.matches('.product-listings')
        ? targetElement
        : targetElement?.querySelector('.product-listings');

    if (!container)
        return;

    let lockScroll = false;

    // Get the current page.
    let page = savedPage ?? parseNumber(element.querySelector('li.active')?.textContent ?? '1');

    // Preload pages up to the current page.
    if (page > 1) {
        for (let p = 2; p <= page; p++) {
            await loadPage(p);
        }
    }

    // Get the total number of pages.
    const totalPages = Array.from(element.querySelectorAll('a.page-link')).filter(a => isNumber(a.textContent)).map(a => parseNumber(a.textContent)).pop();

    window.addEventListener('scroll', async () => {
        // Make sure the page still contains the element (this prevents fetch from being called multiple times when the ajax-list filter is updated).
        if (!document.contains(element))
            return;

        // Get the position.
        const rect = element.getBoundingClientRect();

        // Is the pager in view.
        const isVisible = rect.top < window.innerHeight && rect.bottom >= 0;

        if (!lockScroll && isVisible) {
            // Make sure the total pages has not been reached.
            if (page >= totalPages)
                return;

            // Increment the page.
            page++;

            // Save the current page to session storage.
            sessionStorage.setItem(`currentPage:${getScrollKey()}`, page);

            // Lock the scroll (this prevents the ajax request being executed concurrently).
            lockScroll = true;

            // Load the page.
            await loadPage(page);

            // Unlock the scroll.
            lockScroll = false;
        }
    });

    async function loadPage(p) {
        // Work out the url.
        const url = new URL(location.href);
        url.searchParams.set('page', p);
                
        const response = await fetch(url.pathname + url.search, {
            headers: {
                'X-Requested-With': '' // We want the server to think this is a full request (therefore IsAjaxRequest() will be false).
            }
        });

        if (!response.ok)
            return;

        const html = await response.text();

        // Parse the response.
        const doc = new DOMParser().parseFromString(html, 'text/html');

        // Find the product listings in the fetched page.
        const pageTarget = doc.querySelector(target);

        const pageListings = pageTarget?.matches('.product-listings')
            ? pageTarget
            : pageTarget?.querySelector('.product-listings');

        if (!pageListings)
            return;

        // Extract individual product wrappers, not the grid itself.
        const products = Array.from(pageListings.children)
            .filter(child => child.classList.contains('product'));

        // Append the products directly into the existing grid.
        container.append(...products);
        container.dispatchEvent(new CustomEvent('list:appended', {
            bubbles: true,
            detail: { count: products.length }
        }));
    }
}
